import { useMemo, useState } from 'react';
import { useVault } from './lib/vault';
import { LoginScreen, SetupKeyScreen, UnlockScreen } from './pages/Gate';
import { DashboardPage } from './pages/Dashboard';
import { VaultPage } from './pages/Vault';
import { FixedCostsPage } from './pages/FixedCosts';
import { ForecastPage } from './pages/Forecast';
import { InvoicesPage } from './pages/Invoices';
import { TeamPage } from './pages/Team';
import { OnboardingPage } from './pages/Onboarding';
import { OffboardingPage } from './pages/Offboarding';
import { SettingsPage } from './pages/Settings';
import { Avatar } from './components/ui';
import type { PermissionKey } from '@shared/types';

type View =
  | 'dashboard'
  | 'vault'
  | 'fixed'
  | 'forecast'
  | 'invoices'
  | 'team'
  | 'onboarding'
  | 'offboarding'
  | 'settings';

/**
 * Die Reiter, in Gruppen.
 *
 * Bewusst als Überschriften und nicht als aufklappbare Menüs: bei neun Punkten
 * kostet ein Aufklappen nur einen zusätzlichen Klick und versteckt dabei die
 * Hälfte der Anwendung. Sichtbar bleibt schneller als eingeklappt.
 *
 * Mitglieder sehen ohnehin nur vier Punkte - für sie entfallen die
 * Überschriften, weil sie dann mehr Platz kosten als sie ordnen.
 */
interface NavEntry {
  id: View;
  label: string;
  /** Ohne dieses Recht taucht der Punkt gar nicht erst auf. */
  needs?: PermissionKey;
}

interface NavGroup {
  /** Ohne Titel steht die Gruppe ohne Überschrift oben. */
  title?: string;
  items: NavEntry[];
}

const NAV: NavGroup[] = [
  {
    items: [
      { id: 'dashboard', label: 'Übersicht' },
      { id: 'vault', label: 'Zugänge' },
    ],
  },
  {
    title: 'Kosten',
    items: [
      { id: 'fixed', label: 'Sonstige Kosten', needs: 'viewPayroll' },
      { id: 'forecast', label: 'Prognose', needs: 'viewCosts' },
      { id: 'invoices', label: 'Rechnungen', needs: 'viewCosts' },
    ],
  },
  {
    title: 'Team',
    items: [
      { id: 'team', label: 'Mitglieder' },
      { id: 'onboarding', label: 'Onboarding', needs: 'manageTeam' },
      { id: 'offboarding', label: 'Offboarding', needs: 'manageTeam' },
    ],
  },
  {
    items: [{ id: 'settings', label: 'Einstellungen' }],
  },
];

export function App(): JSX.Element {
  const { status, error, me, can, items, lock } = useVault();
  const [view, setView] = useState<View>('dashboard');
  /** Wenn ein Hinweis auf bestimmte Einträge zeigt, werden nur diese aufgelistet. */
  const [focusIds, setFocusIds] = useState<string[] | null>(null);

  const navigate = (next: View, ids?: string[]): void => {
    setFocusIds(ids ?? null);
    setView(next);
  };

  const rotationCount = useMemo(() => items.filter((i) => i.needsRotation).length, [items]);

  if (status === 'loading') {
    return (
      <div className="gate">
        <div className="gate-card">
          <p className="muted">Wird geladen …</p>
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="gate">
        <div className="gate-card">
          <h1>Da ist etwas schiefgegangen</h1>
          <div className="note note-danger" style={{ marginTop: 12 }}>{error}</div>
          <button className="btn-primary" style={{ marginTop: 14 }} onClick={() => window.location.reload()}>
            Neu laden
          </button>
        </div>
      </div>
    );
  }
  if (status === 'anonymous') return <LoginScreen />;
  if (status === 'needs-setup') return <SetupKeyScreen />;
  if (status === 'locked') return <UnlockScreen />;

  const gruppen = NAV.map((g) => ({
    ...g,
    items: g.items.filter((n) => !n.needs || can(n.needs)),
  })).filter((g) => g.items.length > 0);

  // Für Mitglieder bleiben so wenige Punkte, dass Überschriften nur stören.
  const mitUeberschriften = gruppen.reduce((n, g) => n + g.items.length, 0) > 5;

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="brand">
          {/* Austauschbar über web/public/logo.svg – siehe README. */}
          <img className="brand-logo" src="/logo.svg" alt="WACH" />
        </div>

        <div className="nav-items">
          {gruppen.map((gruppe, index) => (
            <div className="nav-group" key={gruppe.title ?? `gruppe-${index}`}>
              {gruppe.title && mitUeberschriften ? (
                <div className="nav-title">{gruppe.title}</div>
              ) : null}
              {gruppe.items.map((n) => (
                <button
                  key={n.id}
                  className="nav-item"
                  aria-current={view === n.id}
                  onClick={() => navigate(n.id)}
                >
                  {n.label}
                  {n.id === 'vault' && rotationCount > 0 ? (
                    <span className="badge badge-danger">{rotationCount}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="sidebar-foot">
          <div className="who">
            {me ? <Avatar user={me} /> : null}
            <div className="who-text">
              <div className="who-name">{me?.name}</div>
              <div className="who-mail">{me?.email}</div>
            </div>
          </div>
          <button className="btn-quiet btn-sm" style={{ width: '100%' }} onClick={lock}>
            Sperren
          </button>
        </div>
      </nav>

      <main className="main">
        {view === 'dashboard' ? <DashboardPage onNavigate={navigate} /> : null}
        {view === 'vault' ? (
          <VaultPage focusIds={focusIds} onClearFocus={() => setFocusIds(null)} />
        ) : null}
        {view === 'fixed' ? <FixedCostsPage /> : null}
        {view === 'forecast' ? <ForecastPage /> : null}
        {view === 'invoices' ? <InvoicesPage /> : null}
        {view === 'team' ? <TeamPage onNavigate={navigate} /> : null}
        {view === 'onboarding' ? <OnboardingPage startFor={focusIds?.[0] ?? null} /> : null}
        {view === 'offboarding' ? <OffboardingPage startFor={focusIds?.[0] ?? null} /> : null}
        {view === 'settings' ? <SettingsPage /> : null}
      </main>
    </div>
  );
}

export type { View };
